import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { IconBack, InformationalScreen, LoadingModal, Stack, } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ApiStatus, ErrorCode } from 'Constants';
import axios from 'axios';
import { useApiError } from 'Hooks';
import { Button, Footer, MainContainer, OptInOut, RichText } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { getDocumentPathFromLanguage } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { logout } from 'Utilities/session';
import { store } from 'Utilities/store';
import { handleConsent } from 'Services/consent';
const mapStateToProps = ({ env: { osType, osVersion, app, appVersion, phoneLanguage }, auth, nav: { query }, config: { documentUrls, documentPaths }, }) => {
    return {
        osType,
        osVersion,
        app,
        appVersion,
        auth,
        isAccountProfile: !!query.accountProfile,
        cmsDomain: documentUrls.cms,
        hipaaCmsPaths: documentPaths.cms?.hipaalibre,
        phoneLanguage,
    };
};
const connector = connect(mapStateToProps);
const HipaaConsent = ({ osType, osVersion, isAccountProfile, app, appVersion, auth, cmsDomain, hipaaCmsPaths, phoneLanguage, }) => {
    const [loading, setLoading] = useState(false);
    const [isConsentOutdated, setIsConsentOutdated] = useState(false);
    const [hipaaBodyText, setHipaaBodyText] = useState(i18n.t('Hipaa.content.hipaaRichText.bodyText'));
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const fetchHipaaConsentText = useCallback(async () => {
        try {
            const hipaaCmsPath = getDocumentPathFromLanguage(hipaaCmsPaths, phoneLanguage);
            const hipaaCmsUrl = `${cmsDomain}${hipaaCmsPath}`;
            const response = await axios.get(hipaaCmsUrl);
            const data = response.data;
            if (!data?.Hipaa?.content?.hipaaRichText?.bodyText) {
                setLoading(false);
                return;
            }
            setHipaaBodyText(data.Hipaa.content.hipaaRichText.bodyText);
            store.dispatch({
                type: SHOW_MODAL,
                modalProps: {
                    details: i18n.t('Consents.modals.reviewConsent.body'),
                    testID: 'ReviewConsent',
                },
            });
            setIsConsentOutdated(true);
            setLoading(false);
        }
        catch {
            setLoading(false);
        }
    }, [cmsDomain, hipaaCmsPaths, phoneLanguage]);
    // Disable consents runtime update for release/3.23.1
    // useEffect(() => {
    //   if (isAccountProfile && auth.include.patient.consents.hipaa) {
    //     const { at, latestVersion } = auth.include.patient.consents.hipaa;
    //     const isOutdated = at < latestVersion;
    //     if (isOutdated) {
    //       fetchHipaaConsentText();
    //     }
    //   }
    //   setLoading(false);
    // }, [auth, fetchHipaaConsentText, isAccountProfile]);
    const handleApiRequestError = useCallback((error) => {
        switch (error.status) {
            case ApiStatus.UNAUTHORIZED:
                logout();
                break;
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [showApiErrorModal, showNetworkErrorModal]);
    const handleConsentWrapper = useCallback(async (action) => {
        return new Promise((resolve, reject) => {
            handleConsent(auth.account_id, action, osType, osVersion, app, appVersion, 'hipaa')
                .then((resp) => {
                const updatedAuthSession = {
                    ...auth,
                    include: {
                        patient: {
                            ...auth.include.patient,
                            consents: {
                                ...auth.include.patient.consents,
                                hipaa: { ...resp },
                            },
                        },
                    },
                };
                store.dispatch({ type: AUTH_SESSION, authSession: updatedAuthSession });
                notifyApp('adc-webview:consents', {
                    id: 'hipaa',
                    action,
                    location: isAccountProfile ? 'profile' : 'registration',
                });
                resolve(updatedAuthSession);
            })
                .catch((error) => {
                handleApiRequestError(error);
                reject(error);
            });
        });
    }, [app, appVersion, auth, handleApiRequestError, isAccountProfile, osType, osVersion]);
    const handleSubmitHipaaAction = useCallback(async (action) => {
        try {
            setLoading(true);
            const updatedAuthSession = await handleConsentWrapper(action);
            if (!auth.include.patient.consents?.realWorldEvidence?.at) {
                mediator.publish('router:navigate', '/rwe');
                return;
            }
            notifyApp('adc-webview:signin', updatedAuthSession);
            setIsConsentOutdated(false);
            if (isAccountProfile) {
                // Ends the loading state if the user is on the account profile page
                setLoading(false);
                return;
            }
            // This timeout is added to give the app time to redirect the user back.
            setTimeout(() => {
                setLoading(false);
            }, APP_RETURN_TIMEOUT);
        }
        catch (err) {
            setLoading(false);
        }
    }, [auth, handleConsentWrapper, isAccountProfile]);
    const onClosePressed = useCallback(() => {
        mediator.publish('router:back');
    }, []);
    return (React.createElement(MainContainer, null,
        loading && React.createElement(LoadingModal, { "aria-label": "Loading" }),
        React.createElement(InformationalScreen, null,
            React.createElement(InformationalScreen.Page, { topNavigationProps: {
                    animatedTitle: true,
                    title: i18n.t('Hipaa.title'),
                    buttonStart: isAccountProfile && !isConsentOutdated
                        ? {
                            element: React.createElement(IconBack, { testID: 'Hipaa.close-Pressable' }),
                            label: i18n.t('Hipaa.close'),
                            onPress: onClosePressed,
                        }
                        : undefined,
                }, testID: "Hipaa" },
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(RichText, { testID: "Hipaa.content.hipaaRichText.bodyText", text: hipaaBodyText })),
                React.createElement(InformationalScreen.Footer, null, isConsentOutdated || !isAccountProfile ? (React.createElement(Footer, null,
                    React.createElement(Stack, { alignSelf: "flex-end", width: "100%", gap: "$4" },
                        React.createElement(Button, { onPress: () => handleSubmitHipaaAction('accept'), testID: "Hipaa.accept", "aria-label": i18n.t('Global.microcopy.common.accept'), label: i18n.t('Global.microcopy.common.accept') }),
                        React.createElement(Button, { variant: "tertiary", onPress: () => handleSubmitHipaaAction('decline'), label: i18n.t('Global.microcopy.common.decline'), "aria-label": i18n.t('Global.microcopy.common.decline'), testID: "Hipaa.decline" })))) : (React.createElement(OptInOut, { optionStrings: {
                        titleTag: 'Global.microcopy.common.status',
                        positiveTag: 'Global.microcopy.common.accepted',
                        negativeTag: 'Global.microcopy.common.declined',
                        positiveOption: 'Global.microcopy.common.accept',
                        negativeOption: 'Global.microcopy.common.decline',
                    }, initialState: !!auth.include.patient?.consents?.hipaa?.accepted ? 'accept' : 'decline', handleConsent: handleConsentWrapper })))))));
};
export default connector(HipaaConsent);
